import React from "react";
import clsx from "clsx";
import Icon from "../btn/Icon";
import Round from "../badge/Round";
import BubbleSuggestion from "./Suggestion";
import ActionPopover from "./ActionPopover";
import { LikeDownActivated, LikeUpActivated } from "../../assets/svgs/icons";

interface ActionProps {
  visible?: boolean;
  suggestion?: boolean;
  suggestions?: string[];
  userEva?: {
    simple_eva: 0 | 1 | null;
    eva?: string;
  };
  onCopy?: () => void;
  onLike?: () => void;
  onDislike?: () => void;
  onFeedbackSubmit?: (feedback: string, type: "like" | "dislike") => void;
  onRegenerate?: () => void;
  onTts?: () => void;
  onSuggestionClick?: (suggestion: string) => void;
  className?: string;
  // TTS 관련
  isTtsSpeaking?: boolean;
  messageResponseId?: string;
  currentSpeakingMessageId?: string | null;
}

const Action: React.FC<ActionProps> = ({
  visible = true,
  suggestion = false,
  suggestions = [],
  userEva,
  onCopy,
  onLike,
  onDislike,
  onFeedbackSubmit,
  onRegenerate,
  onTts,
  onSuggestionClick,
  className = "",
  isTtsSpeaking = false,
  messageResponseId,
  currentSpeakingMessageId,
}) => {
  if (!visible) return null;

  const isLiked = userEva?.simple_eva === 1;
  const isDisliked = userEva?.simple_eva === 0;

  // 현재 메시지가 재생 중인지 확인
  const isThisMessageSpeaking =
    isTtsSpeaking &&
    messageResponseId &&
    currentSpeakingMessageId === messageResponseId;

  const displaySuggestions = suggestions.length > 0 ? suggestions : [];

  // Suggestion list component
  const SuggestionList = () => {
    if (!suggestion) return null;

    return (
      <div className="basis-0 content-stretch flex flex-col gap-2 grow items-end min-h-0 min-w-0 relative shrink-0">
        {/* Mobile: md size, Desktop: lg size */}
        <div className="block md:hidden">
          <Round size="md" type="primary" style="fill" color="main-gr">
            이런 질문을 할 수 있어요!
          </Round>
        </div>
        <div className="hidden md:block">
          <Round size="lg" type="primary" style="fill" color="main-gr">
            이런 질문을 할 수 있어요!
          </Round>
        </div>
        <div className="content-stretch flex flex-col gap-2 items-end w-full">
          {displaySuggestions.map((suggestionText, index) => (
            <button
              key={index}
              onClick={() => onSuggestionClick?.(suggestionText)}
              className="box-border hover:bg-[#f0eafe] transition-colors rounded-bl-[20px] rounded-br-[20px] rounded-tl-[20px] max-w-[768px] md:max-w-[768px]"
            >
              <BubbleSuggestion content={suggestionText} />
            </button>
          ))}
        </div>
      </div>
    );
  };

  return (
    <div
      className={clsx(
        "box-border relative flex items-start w-full min-h-6",
        className
      )}
    >
      <SuggestionList />
      <LikeUpActivated width={0} height={0} />
      <LikeDownActivated width={0} height={0} />
      {/* Action buttons - positioned absolute left-0 top-0 like in Figma */}
      <div className="absolute left-0 top-0 flex items-end gap-[12px] py-[2px]">
        {/* Mobile: 20px icons, Desktop: 24px icons with gap-[12px] */}
        <div className="flex items-end gap-[12px] md:hidden">
          <Icon
            icon="Copy"
            size="sm"
            style="ghost"
            onClick={onCopy}
            aria-label="Copy"
            className="text-[#b2b2b2] hover:text-[#212121] !size-[20px] !p-0"
          />
          <ActionPopover
            type="like"
            size="sm"
            isActivated={isLiked}
            disabled={isLiked}
            initialFeedback={isLiked ? userEva?.eva : ""}
            onOpen={onLike}
            onSave={(feedback) => onFeedbackSubmit?.(feedback, "like")}
          />
          <ActionPopover
            type="dislike"
            size="sm"
            isActivated={isDisliked}
            disabled={isDisliked}
            initialFeedback={isDisliked ? userEva?.eva : ""}
            onOpen={onDislike}
            onSave={(feedback) => onFeedbackSubmit?.(feedback, "dislike")}
          />
          {onRegenerate ? (
            <Icon
              icon="Reset"
              size="sm"
              style="ghost"
              onClick={onRegenerate}
              aria-label="Regenerate"
              className="text-[#b2b2b2] hover:text-[#212121] !size-[20px] !p-0"
            />
          ) : null}
          <Icon
            icon={isThisMessageSpeaking ? "StopDefault" : "TtsOn"}
            size="sm"
            style="ghost"
            onClick={onTts}
            aria-label="Text to speech"
            className="text-[#b2b2b2] hover:text-[#212121] !size-[20px] !p-0"
          />
        </div>

        <div className="hidden md:flex items-end gap-[12px]">
          <Icon
            icon="Copy"
            size="md"
            style="ghost"
            onClick={onCopy}
            aria-label="Copy"
            className="text-[#b2b2b2] hover:text-[#212121] !size-[24px] !p-0"
          />
          <ActionPopover
            type="like"
            size="md"
            isActivated={isLiked}
            disabled={isLiked}
            initialFeedback={isLiked ? userEva?.eva : ""}
            onOpen={onLike}
            onSave={(feedback) => onFeedbackSubmit?.(feedback, "like")}
          />
          <ActionPopover
            type="dislike"
            size="md"
            isActivated={isDisliked}
            disabled={isDisliked}
            initialFeedback={isDisliked ? userEva?.eva : ""}
            onOpen={onDislike}
            onSave={(feedback) => onFeedbackSubmit?.(feedback, "dislike")}
          />
          {onRegenerate ? (
            <Icon
              icon="Reset"
              size="md"
              style="ghost"
              onClick={onRegenerate}
              aria-label="Regenerate"
              className="text-[#b2b2b2] hover:text-[#212121] !size-[24px] !p-0"
            />
          ) : null}
          <Icon
            icon={isThisMessageSpeaking ? "StopDefault" : "TtsOn"}
            size="md"
            style="ghost"
            onClick={onTts}
            aria-label="Text to speech"
            className="text-[#b2b2b2] hover:text-[#212121] !size-[24px] !p-0"
          />
        </div>
      </div>
    </div>
  );
};

export default Action;
